/**
 * Piechart Component
 * Renders an interactive pie chart using ApexCharts library
 * Displays either population distribution by district or cancer cases by type
 */

import React, { useState } from "react";
import { useEffect } from "react";
import Chart from "react-apexcharts";

/**
 * Piechart component that dynamically generates pie charts based on provided data
 * @param {Object} chartdata - Data containing district/cancer names and cases count
 * @param {Object} displaychart - Object containing chart display options (type)
 */
const Piechart = ({ chartdata, displaychart }) => {
  // State management for pie chart options and data series
  const [pieOptions, setPieOptions] = useState({});
  const [pieSeries, setPieSeries] = useState([]);
  
  // Extract the display chart type value and convert to lowercase for comparison
  const { value } = displaychart || {};
  const chartType = String(value).toLowerCase();
  
  // Determine labels based on chart type (district names or cancer names)
  const categories =
    chartType === "population"
      ? chartdata?.district_names
      : chartdata?.cancer_names;
  // Initialize chart configuration and data whenever chartdata or display value changes
  useEffect(() => {
    if (value === "population") {
      // Configure pie chart for population distribution by district
      setPieOptions({
        chart: {
          type: "pie",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
        },
        labels: chartdata?.district_names || [],
        legend: { position: "bottom" },
        style: {
          fontSize: "24px",
          fontFamily: "Inter",
          fontWeight: "bold",
        },
      });
      setPieSeries(chartdata?.cases_count || []);
    } else {
      // Configure pie chart for cancer type distribution
      setPieOptions({
        chart: {
          type: "pie",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
        },
        labels: chartdata?.cancer_names || [],
        legend: { position: "bottom" },
      });
      setPieSeries(chartdata?.cases_count || []);
    }
  }, [chartdata, value]);
  // Display loading spinner if data is not ready or invalid
  if (
    !chartdata ||
    !categories ||
    !Array.isArray(categories) ||
    categories.length === 0
  ) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border center text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }
  
  // Render the pie chart with configured options and data series
  return (
    <div className="container">
      <Chart
        key={value}
        options={pieOptions}
        series={pieSeries}
        type="pie"
        width={"100%"}
        height={"200%"}
      />
    </div>
  );
};
// Export the Piechart component as the default export
export default Piechart;
